import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { supabase } from "../supabase/supabaseClient";
import { useAuthState } from "../../hooks/useAuthState";
import SessionManager, {
  setCachedLessons,
} from "../SessionManager";
import { fetchLessons } from "./fetchLessons";

const LessonContext = createContext(null);
export const useLessons = () => useContext(LessonContext);

export const LessonProvider = ({ children, showToast }) => {
  const { user } = useAuthState();

  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [errorLessons, setErrorLessons] = useState(null);

  const hasHydrated = useRef(false);

  /* ===============================
     CENTRAL CACHE SYNC
  =============================== */
  const updateLessonCache = useCallback((newList) => {
    if (!Array.isArray(newList)) return;

    setLessons(newList);
    setCachedLessons(newList);
    SessionManager.setSavedLessons(newList);

    console.log(`🧮 [LessonContext] Cache sync → ${newList.length}`);
  }, []);

  const updateLessonCacheSafe = useCallback((updater) => {
    setLessons((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : updater;

      if (!Array.isArray(next)) return prev;

      setCachedLessons(next);
      SessionManager.setSavedLessons(next);

      return next;
    });
  }, []);

  /* ===============================
     INITIAL LOAD
  =============================== */
  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;

      // 🧠 hydrate ONCE
      if (!hasHydrated.current) {
        const saved = SessionManager.getSavedLessons?.();
        if (saved?.length) {
          console.log("🧠 Restoring lessons from cache");
          setLessons(saved);
        }
        hasHydrated.current = true;
      }

      // 🔄 fetch fresh
      const data = await fetchLessons({
        authUser: user,
        authReady: true,
        setLoadingLessons,
        setErrorLessons,
      });

      updateLessonCache(data);
    };

    load();
  }, [user?.id, updateLessonCache, showToast]);

  /* ===============================
     PUBLIC REFRESH
  =============================== */
  const refreshLessons = useCallback(async () => {
    if (!user?.id) return [];

    const data = await fetchLessons({
      authUser: user,
      authReady: true,
      setLoadingLessons,
      setErrorLessons,
      force: true, // 🔥 IMPORTANT
    });

    updateLessonCache(data);
    return data;
  }, [user?.id, updateLessonCache]);

  /* ===============================
     GLOBAL HOOK
  =============================== */
  useEffect(() => {
    window.refreshLessons = refreshLessons;
  }, [refreshLessons]);

  return (
    <LessonContext.Provider
      value={{
        lessons,
        loadingLessons,
        errorLessons,
        updateLessonCache,
        updateLessonCacheSafe,
        refreshLessons,
      }}
    >
      {children}
    </LessonContext.Provider>
  );
};
